import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, DollarSign, Calendar, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { AvatarUploader } from "./AvatarUploader";

interface Employee {
  id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  hire_date: string;
  is_active: boolean;
  role: 'staff' | 'admin';
}

interface EmployeeProfile {
  hourly_rate: number | null;
  salary_type: string | null;
  overtime_rate: number | null;
  payment_method: string | null;
  avatar_url: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  emergency_contact_relation: string | null;
  medical_notes: string | null;
}

interface TimeEntry {
  id: string;
  clock_in_time: string;
  clock_out_time: string | null;
  total_hours: number | null;
}

interface PayrollCalculation {
  gross_pay: number;
  net_pay: number;
}

interface EmployeeProfileHeaderProps {
  employee: Employee;
  profile: EmployeeProfile | null;
  timeEntries: TimeEntry[];
  payrollHistory: PayrollCalculation[];
  onDataUpdate: () => void;
}

export const EmployeeProfileHeader: React.FC<EmployeeProfileHeaderProps> = ({
  employee,
  profile,
  timeEntries,
  payrollHistory,
  onDataUpdate
}) => {
  // Calculate total hours for the current period
  const totalHours = timeEntries.reduce((sum, entry) => {
    if (entry.total_hours) {
      return sum + entry.total_hours;
    }
    // Calculate if not stored
    if (entry.clock_in_time && entry.clock_out_time) {
      const clockIn = new Date(entry.clock_in_time);
      const clockOut = new Date(entry.clock_out_time);
      return sum + (clockOut.getTime() - clockIn.getTime()) / (1000 * 60 * 60);
    }
    return sum;
  }, 0);

  // Calculate total earnings
  const totalEarnings = payrollHistory.reduce((sum, payroll) => sum + (payroll.net_pay || 0), 0);

  // Get initials for avatar
  const initials = `${employee.first_name.charAt(0)}${employee.last_name.charAt(0)}`.toUpperCase();

  return (
    <div className="space-y-6">
      {/* Employee Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <AvatarUploader
          employeeId={employee.id}
          currentAvatarUrl={profile?.avatar_url}
          employeeName={`${employee.first_name} ${employee.last_name}`}
          onAvatarUpdate={onDataUpdate}
          className="h-16 w-16"
        />
        
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
            <h1 className="text-2xl font-bold">{employee.first_name} {employee.last_name}</h1>
            <div className="flex items-center gap-2">
              <Badge variant={employee.is_active ? "default" : "secondary"}>
                {employee.is_active ? "Active" : "Inactive"}
              </Badge>
              <Badge variant={employee.role === 'admin' ? "destructive" : "outline"}>
                {employee.role === 'admin' ? 'Administrator' : 'Staff'}
              </Badge>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 text-sm text-muted-foreground">
            <span>ID: {employee.employee_id}</span>
            <span>Hired: {format(new Date(employee.hire_date), 'MMM dd, yyyy')}</span>
            {employee.email && <span>{employee.email}</span>}
            {employee.phone && <span>{employee.phone}</span>}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Hours (30 days)</p>
                <p className="text-xl font-bold">{totalHours.toFixed(1)}h</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Hourly Rate</p>
                <p className="text-xl font-bold">
                  ${profile?.hourly_rate?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <TrendingUp className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <p className="text-xl font-bold">${totalEarnings.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <Calendar className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Years of Service</p>
                <p className="text-xl font-bold">
                  {Math.floor((Date.now() - new Date(employee.hire_date).getTime()) / (1000 * 60 * 60 * 24 * 365.25))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};